import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'
import { dashboardService } from '@/lib/dashboardService'
import { useAuth } from '@/contexts/AuthContext'
import { Link } from 'react-router-dom'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { BookOpen, Presentation, Users, TrendingUp, FileText, AlertTriangle, AlertCircle, CheckCircle2, Info, Sparkles } from 'lucide-react'

export const TeacherDashboard: React.FC = () => {
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [students, setStudents] = useState<any[]>([])
  const [scores, setScores] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>('all')
  const [examFilesCount, setExamFilesCount] = useState<number>(0)


  const loadTeacherData = async () => {
    if (!user) return
    setLoading(true)
    try {
      try {
        await dashboardService.syncEnrollments()
      } catch (syncErr) {
        console.warn('Sync enrollments failed silently:', syncErr)
      }
      const [
        allAssignments,
        allSubjects,
        allClassrooms,
        allStudents,
        allScores,
        filesCount
      ] = await Promise.all([
        dataService.getTeachingAssignments(),
        dataService.getSubjects(),
        dataService.getClassrooms(),
        dataService.getStudents(),
        dataService.getScores(),
        dataService.getExamFilesCount()
      ])

      const myAssignments = allAssignments.filter((a: any) => a.teacher_id === user.id)
      setAssignments(myAssignments)
      setSubjects(allSubjects)
      setClassrooms(allClassrooms)
      setStudents(allStudents)
      setScores(allScores)
      setExamFilesCount(filesCount)


    } catch (err) {
      console.error('Error loading teacher dashboard statistics:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTeacherData()
  }, [user])

  const getSubjectInfo = (id: number) => {
    const s = subjects.find(sub => sub.id === id)
    return s ? `${s.code} ${s.name}` : 'ไม่ทราบวิชา'
  }

  const getClassroomName = (id: number) => {
    const c = classrooms.find(cl => cl.id === id)
    return c ? `ม.${c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/${c.room_number}` : 'ไม่ทราบห้อง'
  }

  // Filtered assignments based on dropdown selection
  const filteredAssignments = selectedAssignmentId === 'all'
    ? assignments
    : assignments.filter((a: any) => String(a.id) === selectedAssignmentId)

  // Calculate stats on-the-fly for displaying
  const getDisplayStats = () => {
    const mySubjectIds = filteredAssignments.map((a: any) => a.subject_id)
    const myClassroomIds = filteredAssignments.map((a: any) => a.classroom_id)

    const uniqueSubjectIds = [...new Set(mySubjectIds)]
    const uniqueClassroomIds = [...new Set(myClassroomIds)]

    const activeStudents = students.filter((st: any) => 
      myClassroomIds.includes(st.classroom_id) && 
      (!st.status || st.status === 'active')
    )

    const activeStudentIds = activeStudents.map((st: any) => st.id)
    const myClassScores = scores.filter((sc: any) => 
      mySubjectIds.includes(sc.subject_id) && 
      activeStudentIds.includes(sc.student_id)
    )

    let preSum = 0, preCount = 0
    let postSum = 0, postCount = 0

    myClassScores.forEach((sc: any) => {
      if (sc.pre_test_score !== null && sc.pre_test_score !== undefined) {
        preSum += Number(sc.pre_test_score)
        preCount++
      }
      if (sc.post_test_score !== null && sc.post_test_score !== undefined) {
        postSum += Number(sc.post_test_score)
        postCount++
      }
    })

    const preAvg = preCount > 0 ? preSum / preCount : 0
    const postAvg = postCount > 0 ? postSum / postCount : 0
    const overallGain = postAvg - preAvg

    return {
      subjectsCount: uniqueSubjectIds.length,
      roomsCount: uniqueClassroomIds.length,
      studentsCount: activeStudents.length,
      overallGain: overallGain.toFixed(2)
    }
  }

  const displayStats = getDisplayStats()

  // Get Alerts for abnormal situations (intelligent alerts system)
  const getAlerts = () => {
    const alertsList: any[] = []
    filteredAssignments.forEach((assign: any) => {
      const classSts = students.filter(st => st.classroom_id === assign.classroom_id && (!st.status || st.status === 'active'))
      const classStIds = classSts.map(st => st.id)
      const classScores = scores.filter(sc => sc.subject_id === assign.subject_id && classStIds.includes(sc.student_id))

      let preCount = classScores.filter(sc => sc.pre_test_score !== null && sc.pre_test_score !== undefined).length
      let postCount = classScores.filter(sc => sc.post_test_score !== null && sc.post_test_score !== undefined).length

      let preSum = 0, postSum = 0
      classScores.forEach(sc => {
        preSum += Number(sc.pre_test_score || 0)
        postSum += Number(sc.post_test_score || 0)
      })

      const preAvg = preCount > 0 ? preSum / preCount : 0
      const postAvg = postCount > 0 ? postSum / postCount : 0
      const gain = postAvg - preAvg

      const sub = subjects.find(s => s.id === assign.subject_id)
      const subName = sub ? `${sub.code} ${sub.name}` : '—'
      const roomName = getClassroomName(assign.classroom_id)
      const maxScorePre = sub ? Number(sub.max_score_pre) : 10
      const maxScorePost = sub ? Number(sub.max_score_post) : 10

      if (classSts.length > 0) {
        if (preCount === 0) {
          alertsList.push({
            id: `pre-empty-${assign.id}`,
            type: 'danger',
            message: `⚠️ ยังไม่ได้บันทึกคะแนน Pre-Test สำหรับวิชา ${subName} (${roomName})`
          })
        } else if (postCount === 0) {
          alertsList.push({
            id: `post-empty-${assign.id}`,
            type: 'warning',
            message: `📝 ยังไม่ได้บันทึกคะแนน Post-Test สำหรับวิชา ${subName} (${roomName})`
          })
        }
        
        if (preCount > 0 && postCount > 0) {
          if (gain < 0) {
            alertsList.push({
              id: `low-gain-${assign.id}`,
              type: 'danger',
              message: `🚨 พัฒนาการติดลบ! คะแนนเฉลี่ยลดลงหลังเรียนในวิชา ${subName} (${roomName}): ${gain.toFixed(2)}`
            })
          } else if (gain >= (maxScorePost * 0.25)) {
            alertsList.push({
              id: `outstanding-gain-${assign.id}`,
              type: 'success',
              message: `🎉 ชื่นชมความสำเร็จ! นักเรียนห้อง ${roomName} มีพัฒนาการทางการเรียน (Learning Gain) ที่โดดเด่นในวิชา ${subName}: +${gain.toFixed(2)}`
            })
          }
        }
        
        if (preCount > 0 && preAvg < (maxScorePre * 0.4)) {
          alertsList.push({
            id: `low-pre-avg-${assign.id}`,
            type: 'info',
            message: `💡 ข้อสังเกต: คะแนนเฉลี่ย Pre-Test ของห้อง ${roomName} ในวิชา ${subName} ค่อนข้างต่ำ (${preAvg.toFixed(1)} / ${maxScorePre})`
          })
        }
      }
    })
    return alertsList
  }

  const alerts = getAlerts()

  // Get 5 recent score records
  const getRecentScores = () => {
    const mySubjectIds = filteredAssignments.map((a: any) => a.subject_id)
    const myClassroomIds = filteredAssignments.map((a: any) => a.classroom_id)
    const activeStudentIds = students
      .filter((st: any) => myClassroomIds.includes(st.classroom_id) && (!st.status || st.status === 'active'))
      .map((st: any) => st.id)
      
    const myScores = scores.filter((sc: any) => 
      mySubjectIds.includes(sc.subject_id) && 
      activeStudentIds.includes(sc.student_id)
    )
    return myScores.slice(-5).reverse()
  }

  const recentScoresList = getRecentScores()

  const getStudentName = (id: number) => {
    const st = students.find(s => s.id === id)
    return st ? `${st.title || 'เด็กชาย'}${st.first_name} ${st.last_name}` : 'ไม่ทราบชื่อ'
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  const getChartData = () => {
    return assignments.map((assign: any) => {
      const classSts = students.filter(st => st.classroom_id === assign.classroom_id && (!st.status || st.status === 'active'))
      const classStIds = classSts.map(st => st.id)
      const classScores = scores.filter(sc => sc.subject_id === assign.subject_id && classStIds.includes(sc.student_id))

      let preSum = 0, preCount = 0
      let postSum = 0, postCount = 0
      classScores.forEach(sc => {
        if (sc.pre_test_score !== null && sc.pre_test_score !== undefined) {
          preSum += Number(sc.pre_test_score)
          preCount++
        }
        if (sc.post_test_score !== null && sc.post_test_score !== undefined) {
          postSum += Number(sc.post_test_score)
          postCount++
        }
      })

      const preAvg = preCount > 0 ? preSum / preCount : 0
      const postAvg = postCount > 0 ? postSum / postCount : 0
      const gain = postAvg - preAvg
      const sub = subjects.find(s => s.id === assign.subject_id)
      const maxVal = sub ? Math.max(sub.max_score_pre, sub.max_score_post) : 10
      const prePercent = maxVal > 0 ? (preAvg / maxVal) * 100 : 0
      const postPercent = maxVal > 0 ? (postAvg / maxVal) * 100 : 0

      const classroom = classrooms.find(c => c.id === assign.classroom_id)
      const classroomName = classroom ? `ม.${classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level}/${classroom.room_number}` : '—'
      const subCode = sub?.code || '—'

      return {
        id: assign.id,
        label: `${subCode} (${classroomName})`,
        preAvg,
        postAvg,
        gain,
        prePercent,
        postPercent
      }
    })
  }

  const chartData = getChartData()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      
      {/* Welcome Banner Card */}
      <div className="glassmorphism" style={{
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(99, 102, 241, 0.95))',
        padding: '32px',
        borderRadius: '24px',
        color: '#ffffff',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 10px 30px rgba(99, 102, 241, 0.15)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '24px'
      }}>
        <div style={{ flex: 1, minWidth: '280px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px' }}>✨</span>
            <h1 style={{ fontSize: '22px', fontWeight: '800', margin: 0, letterSpacing: '-0.5px', color: '#ffffff' }}>
              สวัสดีครับ/ค่ะ คุณครู{user?.first_name} {user?.last_name}
            </h1>
          </div>
          <p style={{ color: 'rgba(255, 255, 255, 0.85)', margin: 0, fontSize: '13px', lineHeight: '1.6' }}>
            ยินดีต้อนรับกลับสู่แผงวิเคราะห์ผลสัมฤทธิ์การสอน วันนี้คุณครูรับผิดชอบดูแลการสอน <strong>{assignments.length} ห้องเรียน</strong> รวมจำนวนนักเรียนทั้งหมด <strong>{displayStats.studentsCount} คน</strong>
          </p>
        </div>
        <Link 
          to="/teacher/entry" 
          className="btn" 
          style={{ 
            background: '#ffffff', 
            color: 'var(--primary)', 
            fontWeight: '700', 
            padding: '12px 24px', 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none',
            fontSize: '13px'
          }}
        >
          ✏️ ป้อนคะแนนห้องเรียนสอบ ➔
        </Link>
      </div>

      {/* Subject & Classroom Filter Selector */}
      <div className="glassmorphism" style={{ 
        padding: '20px 24px', 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '16px', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderRadius: '16px',
        border: '1px solid var(--border)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: 'rgba(59, 130, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary)'
          }}>
            <Sparkles size={18} />
          </div>
          <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
            กรองวิเคราะห์รายห้องเรียน:
          </span>
          <select 
            value={selectedAssignmentId} 
            onChange={(e) => setSelectedAssignmentId(e.target.value)}
            className="input-field" 
            style={{ 
              width: 'auto', 
              minWidth: '320px', 
              margin: 0, 
              padding: '8px 32px 8px 12px', 
              fontSize: '14px', 
              borderRadius: '10px',
              background: 'var(--bg-input)',
              border: '1px solid var(--border)',
              fontWeight: '600'
            }}
          >
            <option value="all">🌐 แสดงภาพรวมผลสัมฤทธิ์ทุกห้องเรียน ({assignments.length} ห้อง)</option>
            {assignments.map((assign: any) => {
              const sub = subjects.find(s => s.id === assign.subject_id)
              const subText = sub ? `${sub.code} ${sub.name}` : 'ไม่ทราบวิชา'
              const roomName = getClassroomName(assign.classroom_id)
              return (
                <option key={assign.id} value={String(assign.id)}>
                  📖 {subText} — ห้อง {roomName}
                </option>
              )
            })}
          </select>
        </div>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600', background: 'rgba(255, 255, 255, 0.05)', padding: '6px 12px', borderRadius: '20px' }}>
          💡 สลับห้องเรียนด้านซ้ายเพื่อดูพัฒนาการแบบเจาะลึก
        </span>
      </div>

      {/* Abnormal Alerts Section */}
      {alerts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
            📢 ข้อเสนอแนะและข้อสังเกตอัจฉริยะ ({alerts.length} รายการ)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px' }}>
            {alerts.map(al => {
              let alertBg = 'rgba(59, 130, 246, 0.04)'
              let alertBorder = 'var(--primary)'
              let alertColor = 'var(--primary)'
              let alertIcon = <Info size={16} />

              if (al.type === 'danger') {
                alertBg = 'rgba(239, 68, 68, 0.05)'
                alertBorder = 'var(--danger)'
                alertColor = 'var(--danger)'
                alertIcon = <AlertCircle size={16} />
              } else if (al.type === 'warning') {
                alertBg = 'rgba(245, 158, 11, 0.05)'
                alertBorder = 'var(--warning)'
                alertColor = 'var(--warning)'
                alertIcon = <AlertTriangle size={16} />
              } else if (al.type === 'success') {
                alertBg = 'rgba(16, 185, 129, 0.05)'
                alertBorder = 'var(--success)'
                alertColor = 'var(--success)'
                alertIcon = <CheckCircle2 size={16} />
              }

              return (
                <div key={al.id} style={{
                  background: alertBg,
                  border: `1px solid ${alertBorder}33`,
                  borderLeft: `4px solid ${alertBorder}`,
                  borderRadius: '12px',
                  padding: '14px 18px',
                  fontSize: '13px',
                  color: alertColor,
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  lineHeight: '1.4'
                }}>
                  <span style={{ marginTop: '2px', flexShrink: 0 }}>{alertIcon}</span>
                  <span>{al.message}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px'
      }}>
        {/* Card 1: Subjects */}
        <div className="glassmorphism hover-card-effect" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-card)', transition: 'transform 0.2s' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={22} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>รายวิชาที่สอน</span>
            <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)' }}>{displayStats.subjectsCount} วิชา</span>
          </div>
        </div>

        {/* Card 2: Classrooms */}
        <div className="glassmorphism hover-card-effect" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-card)', transition: 'transform 0.2s' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Presentation size={22} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>ห้องเรียนทั้งหมด</span>
            <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)' }}>{displayStats.roomsCount} ห้อง</span>
          </div>
        </div>

        {/* Card 3: Students */}
        <div className="glassmorphism hover-card-effect" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-card)', transition: 'transform 0.2s' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(245, 158, 11, 0.1)', color: 'var(--warning)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={22} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>นักเรียนในความดูแล</span>
            <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)' }}>{displayStats.studentsCount} คน</span>
          </div>
        </div>

        {/* Card 4: Learning Gain */}
        <div className="glassmorphism hover-card-effect" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-card)', transition: 'transform 0.2s' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            borderRadius: '14px', 
            background: displayStats.overallGain.startsWith('-') ? 'rgba(239, 68, 68, 0.1)' : 'rgba(129, 140, 248, 0.1)', 
            color: displayStats.overallGain.startsWith('-') ? 'var(--danger)' : '#6366f1', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <TrendingUp size={22} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>พัฒนาการรวม (Gain)</span>
            <span style={{ 
              fontSize: '24px', 
              fontWeight: '800', 
              color: displayStats.overallGain.startsWith('-') ? 'var(--danger)' : 'var(--success)' 
            }}>
              {displayStats.overallGain.startsWith('-') ? '' : '+'}{displayStats.overallGain} คะแนน
            </span>
          </div>
        </div>

        {/* Card 5: Files */}
        <div className="glassmorphism hover-card-effect" style={{ padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', borderRadius: '20px', border: '1px solid var(--border)', background: 'var(--bg-card)', transition: 'transform 0.2s' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FileText size={22} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>คลังไฟล์ข้อสอบ</span>
            <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-primary)' }}>{examFilesCount} ไฟล์</span>
            <Link to="/teacher/exam-repository" style={{ fontSize: '11px', color: 'var(--primary)', fontWeight: '700', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '2px', marginTop: '2px' }}>
              เปิดคลัง ➔
            </Link>
          </div>
        </div>
      </div>

      {/* 2-column content: Trends & Recent list */}
      <div className="grid-responsive-dashboard">
        
        {/* Trend / comparative chart block */}
        <div className="glassmorphism" style={{ padding: '28px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px', color: 'var(--text-primary)' }}>📊 คะแนนเฉลี่ย Pre-Test และ Post-Test จำแนกรายห้อง</h2>
          {chartData.length > 0 ? (
            <div style={{ padding: '10px 0' }}>
              <div style={{ height: '320px', width: '100%', marginTop: '10px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" stroke="var(--text-secondary)" fontSize={11} tickLine={false} />
                    <YAxis unit="%" stroke="var(--text-secondary)" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'var(--bg-card)', 
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        color: 'var(--text-primary)' 
                      }} 
                    />
                    <Legend verticalAlign="top" height={36} />
                    <Bar name="คะแนนเฉลี่ย Pre-Test (%)" dataKey="prePercent" fill="var(--secondary)" radius={[4, 4, 0, 0]} />
                    <Bar name="คะแนนเฉลี่ย Post-Test (%)" dataKey="postPercent" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '24px' }}>📭 คุณครูยังไม่มีวิชาเรียนสอน</div>
          )}
        </div>

        {/* Recent Score entries list */}
        <div className="glassmorphism" style={{ padding: '28px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '20px', color: 'var(--text-primary)' }}>📝 รายการป้อนคะแนนล่าสุด</h2>
          {recentScoresList.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'center', padding: '24px' }}>📭 ยังไม่มีข้อมูลบันทึกคะแนนในขณะนี้</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {recentScoresList.map((sc, idx) => {
                const studentName = getStudentName(sc.student_id)
                const rawName = studentName.replace(/^(เด็กชาย|เด็กหญิง|นาย|นางสาว|นาง)/, '')
                const initials = rawName.substring(0, 1) || 'น'
                
                // Color picker based on name character hash
                const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4']
                const hash = studentName.split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0)
                const avatarColor = colors[hash % colors.length]

                return (
                  <div key={idx} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '14px', 
                    borderBottom: '1px solid var(--border)', 
                    paddingBottom: '14px',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      background: `${avatarColor}15`,
                      color: avatarColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '13px',
                      border: `1px solid ${avatarColor}22`,
                      flexShrink: 0
                    }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: '13px', display: 'block', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {studentName}
                      </strong>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                        วิชา: {getSubjectInfo(sc.subject_id)}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'block', marginBottom: '2px' }}>Pre / Post</span>
                      <span style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text-primary)' }}>
                        <span style={{ color: 'var(--primary)' }}>{sc.pre_test_score ?? '—'}</span>
                        <span style={{ color: 'var(--text-secondary)', margin: '0 4px' }}>/</span>
                        <span style={{ color: 'var(--success)' }}>{sc.post_test_score ?? '—'}</span>
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

      </div>

    </div>
  )
}
export default TeacherDashboard
